import { CSSProperties } from 'react';
import styled, { css, keyframes } from 'styled-components';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import { Button as JigsawButton } from '@solvhealth/jigsaw';
import { colors } from '~/constants/colors';
import FileUpload from '~/components/SolvPatternLibrary/FileUpload';
import Button from '~/components/SolvPatternLibrary/Button';

/**
 * This file contains the pre-H2 2022 Booking Flow components used in the InsuranceForm, which itself used
 * in all of our various non-SRP/CDP Booking Flows. Compare with ./H22022CardComponents.tsx
 */

export const CARD_WIDTH = 328;

export const BaseCard = styled(Card)`
  width: ${CARD_WIDTH}px;
  height: 192px;
  border-radius: 12px;
  position: relative;
  box-shadow: none;
`;

export type CardState = 'blank' | 'placeholder' | 'photo';

export type CardProps = {
  sc: Pick<CSSProperties, 'borderStyle' | 'borderColor'> & { disabled?: boolean };
  state: CardState;
};

const StyledCard = styled(BaseCard)<CardProps>`
  text-align: center;
  overflow: visible;
  margin-top: 15px;
  cursor: pointer;
  box-shadow: none;
  ${(props) => css`
    border: 1px ${props.sc.borderStyle} ${props.sc.borderColor || colors.black};
    opacity: ${props.sc.disabled ? 0.5 : 1};
  `};
`;

const StyledCardPicture = styled(BaseCard)<{ sc: { imgData?: string } }>`
  background: ${colors.white} url(${(props) => props.sc.imgData}) no-repeat center;
  background-size: cover;
  overflow: hidden;
  -webkit-mask-image: -webkit-radial-gradient(white, black);
`;

const scanKeyframe = keyframes`
  from { transform: translateX(-${CARD_WIDTH}px); }
  to { transform: translateX(0); }
`;

const scanAnimation = css`
  animation: ${scanKeyframe} 2s linear infinite alternate;
`;

const StyledScanOverlay = styled(BaseCard)<{ sc: { selfAnimate: boolean } }>`
  border-radius: 0;
  background-color: ${colors.blueJayWay};
  opacity: 0.5;
  transform: translateX(-${CARD_WIDTH}px);
  ${({ sc }) => sc.selfAnimate && scanAnimation}
`;

const StyledScanHead = styled.div<{ sc: { selfAnimate: boolean } }>`
  height: 210px;
  z-index: 1;
  box-shadow: 0 0 2px ${colors.greyHounds};
  width: 5px;
  position: absolute;
  transform: translateX(-${CARD_WIDTH}px);
  right: -2.5px;
  top: -9px;
  background-color: ${colors.white};
  margin: auto 0;
  ${({ sc }) => sc.selfAnimate && scanAnimation}
`;

const StyledCardHeader = styled(Typography)`
  color: ${colors.fadeToBlack};
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 0.38px;
  line-height: 27px;
`;

const StyledCardBody = styled(Typography)`
  color: ${colors.blueMonday};
  font-size: 14px;
  letter-spacing: -0.2px;
  line-height: 20px;
`;

const StyledUploadButton = styled(Button)`
  width: 80% !important;
  height: 45px;
  padding: 0;
  margin-top: 20px;
`;

const StyledInput = styled(FileUpload)`
  width: ${CARD_WIDTH}px;
  height: 192px;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0.01;
  border: none;
  z-index: 10;
`;

const HintContainer = styled.div`
  text-align: left;
  margin-left: 20px;
`;

const Container = styled.div`
  margin: 0 auto;
  display: inline-flex;
  align-items: center;
  width: 90%;
  margin-top: 20px;
`;

const CardPlaceholder = styled.img`
  height: 58px;
  width: 43px;
`;

const UploadJigsawButton = styled(JigsawButton)`
  width: 80% !important;
  height: 45px;
  padding: 0;
  margin-top: 20px;
`;

export default {
  Card: StyledCard,
  CardPicture: StyledCardPicture,
  ScanOverlay: StyledScanOverlay,
  ScanHead: StyledScanHead,
  CardHeader: StyledCardHeader,
  CardBody: StyledCardBody,
  HintContainer,
  CardContainer: Container,
  CardPlaceholder,
  /**
   * There's a difference between these because for the back of the insurance card,
   * the original design uses the button variation "tertiary" which is a blue that doesn't
   * exist in the jigsaw colors.
   */
  UploadButton: StyledUploadButton,
  UploadButtonJigsaw: UploadJigsawButton,
  Input: StyledInput,
};
